/** The Flow Designer tab — the wizard drawn and edited the way IOFlow designs
    a flow (`../IOFlow/frontend/src/pages/FlowDesigner.js`): a React Flow canvas
    with dots, controls and a minimap; IOFlow's "Add Node" panel (click to add
    after the selected box, or drag onto the canvas); its node cards and edges
    (`./nodes`, `./edges`); its properties panel that docks right, centres or
    goes full screen; undo / redo; and a Validate button that marks the boxes
    with the Check's findings.

    It is a second face on the same model the Steps tab edits — nothing here is
    a separate store. An arrow IS a route key of a step (see `./graph`), the
    properties panel IS the Steps tab's inspector, and the Start box opens Base
    & Mode. Box positions are presentation only and saved beside the model. */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  Background, BackgroundVariant, Controls, MarkerType, MiniMap, Panel, ReactFlow,
  ReactFlowProvider, useEdgesState, useNodesState, useReactFlow,
} from '@xyflow/react'
import type { Connection, IsValidConnection, NodeChange, OnBeforeDelete } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import './wizardFlow.css'
import toast from 'react-hot-toast'
import {
  AlertTriangle, CheckCircle2, Columns2, LayoutGrid, Loader2, Map as MapIcon, Maximize, Maximize2,
  Plus, Redo2, Square, Trash2, Undo2, X,
} from 'lucide-react'
import type { WizardCheckResult, WizardLayout } from '@/api/wizards'
import { useThemeStore } from '@/store/themeStore'
import { useDesignHistory } from '@/hooks/useDesignHistory'
import StepInspector from '../StepInspector'
import BaseTab from '../BaseTab'
import { useWizardDesign } from '../context'
import type { WizardModel, WizardStep, WizardStepKind } from '../types'
import { KIND_META, STEP_KINDS, blankStep, newStepId, renameStep } from '../types'
import { hintStyle } from '../ui'
import type { Category } from './graph'
import {
  CATEGORY_LABEL, CATEGORY_OF, CATEGORY_TONE, FINISH_ID, NODE_W, START_ID, autoLayout,
  connectRoute, disconnectRoute, hasNextHandle, nodeHeight, portTakesFinish, positions,
  removeStep, renameInLayout, routeEdges,
} from './graph'
import type { NodeIssues, WizardFlowNode } from './nodes'
import { nodeTypes } from './nodes'
import type { WizardFlowEdge } from './edges'
import { edgeTypes } from './edges'

const STEP_MIME = 'application/x-twasta-wizard-step'
const TERMINAL_W = 220
const MINIMAP_COLOR: Record<string, string> = { wzStart: CATEGORY_TONE.start, wzFinish: CATEGORY_TONE.finish }

type PanelMode = 'right' | 'center' | 'fullscreen'
const PANEL_MODES: PanelMode[] = ['right', 'center', 'fullscreen']
const PANEL_MODE_KEY = 'twasta.wizardFlow.panelMode'

function readPanelMode(): PanelMode {
  try {
    const v = localStorage.getItem(PANEL_MODE_KEY) as PanelMode | null
    return v && PANEL_MODES.includes(v) ? v : 'right'
  } catch {
    return 'right'
  }
}

export interface WizardFlowDesignerProps {
  onChange: (m: WizardModel) => void
  selected: string | null
  onSelect: (id: string | null) => void
  layout: WizardLayout
  /** A box moved / resized / was added. Presentation only — not a model edit. */
  onLayoutChange: (layout: WizardLayout) => void
  /** The latest Check (from Validate or the last save). */
  check: WizardCheckResult | null
  onValidate: () => Promise<void>
  /** Bumped when the model is (re)loaded — clears undo history. */
  historyKey: number
}

export default function WizardFlowDesigner(props: WizardFlowDesignerProps) {
  return (
    <ReactFlowProvider>
      <FlowCanvas {...props} />
    </ReactFlowProvider>
  )
}

function issuesByStep(check: WizardCheckResult | null): { byStep: Map<string, NodeIssues>; general: NodeIssues } {
  const byStep = new Map<string, NodeIssues>()
  const general: NodeIssues = { errors: [], warnings: [] }
  const add = (step: string | null | undefined, msg: string, kind: 'errors' | 'warnings') => {
    if (!step) { general[kind].push(msg); return }
    if (!byStep.has(step)) byStep.set(step, { errors: [], warnings: [] })
    byStep.get(step)![kind].push(msg)
  }
  for (const e of check?.errors || []) add(e.step, e.message, 'errors')
  for (const w of check?.warnings || []) add(w.step, w.message, 'warnings')
  return { byStep, general }
}

function FlowCanvas({
  onChange, selected, onSelect, layout, onLayoutChange, check, onValidate, historyKey,
}: WizardFlowDesignerProps) {
  const ctx = useWizardDesign()
  const { model, readOnly, project } = ctx
  const theme = useThemeStore((s) => s.theme)
  const rf = useReactFlow<WizardFlowNode, WizardFlowEdge>()
  const modelRef = useRef(model)
  modelRef.current = model
  const layoutRef = useRef(layout)
  layoutRef.current = layout

  const [showPalette, setShowPalette] = useState(false)
  const [panelOpen, setPanelOpen] = useState(false)
  const [panelMode, setPanelMode] = useState<PanelMode>(readPanelMode)
  const [showMiniMap, setShowMiniMap] = useState(true)
  const [validating, setValidating] = useState(false)

  // A (re)load clears undo history. Declared BEFORE the history hook on
  // purpose: effects run in order, so the reset lands first and the hook then
  // baselines on the freshly loaded model (after it, the reset would wipe that
  // baseline and the first edit would go unrecorded).
  const resetHistory = useRef<(() => void) | null>(null)
  const firstHistoryKey = useRef(true)
  useEffect(() => {
    if (firstHistoryKey.current) { firstHistoryKey.current = false; return }
    resetHistory.current?.()
  }, [historyKey])
  const history = useDesignHistory(model, onChange, { disabled: readOnly })
  resetHistory.current = history.reset

  // ── model + layout → nodes / edges ──────────────────────────────────────
  const { byStep, general } = useMemo(() => issuesByStep(check), [check])
  const derivedNodes = useMemo<WizardFlowNode[]>(() => {
    const pos = positions(model, layout)
    const stored = layout.nodes || {}
    const width = (id: string, dflt: number) => stored[id]?.w ?? dflt
    const out: WizardFlowNode[] = [{
      id: START_ID, type: 'wzStart', position: pos[START_ID], width: width(START_ID, TERMINAL_W), deletable: false,
      data: {
        title: model.title || model.obj_name, mode: model.mode, baseObject: model.base_object,
        inputs: (model.inputs || []).map((i) => i.name).filter(Boolean), readOnly,
      },
    }]
    for (const s of model.steps) {
      out.push({
        id: s.id, type: 'wzStep', position: pos[s.id], width: width(s.id, NODE_W), deletable: !readOnly,
        data: {
          step: s, category: CATEGORY_OF[s.kind] || 'system', isStart: s.id === model.start,
          issues: byStep.get(s.id), project, readOnly,
        },
      })
    }
    out.push({
      id: FINISH_ID, type: 'wzFinish', position: pos[FINISH_ID], width: width(FINISH_ID, TERMINAL_W), deletable: false,
      data: { then: model.finish?.then || 'restart' },
    })
    return out
  }, [model, layout, byStep, project, readOnly])

  const derivedEdges = useMemo<WizardFlowEdge[]>(() => routeEdges(model).map((r) => ({
    id: r.id,
    type: 'wzRoute',
    source: r.from,
    sourceHandle: r.port,
    target: r.to,
    targetHandle: 'in',
    data: { route: r },
    deletable: !readOnly && !r.readonly && !r.implicit && r.from !== START_ID,
    reconnectable: !readOnly && !r.readonly,
    focusable: !r.readonly,
    markerEnd: { type: MarkerType.ArrowClosed, width: 16, height: 16 },
  })), [model, readOnly])

  const [nodes, setNodes, onNodesChangeBase] = useNodesState<WizardFlowNode>(derivedNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState<WizardFlowEdge>(derivedEdges)

  // Re-derive on every model / layout change, keeping what React Flow owns
  // (selection, measured size) so a model edit does not drop the selection.
  useEffect(() => {
    setNodes((prev) => {
      const byId = new Map(prev.map((n) => [n.id, n]))
      return derivedNodes.map((d) => {
        const p = byId.get(d.id)
        return { ...d, selected: d.id === selected, measured: p?.measured } as WizardFlowNode
      })
    })
  }, [derivedNodes, selected, setNodes])
  useEffect(() => {
    setEdges((prev) => {
      const sel = new Set(prev.filter((e) => e.selected).map((e) => e.id))
      return derivedEdges.map((e) => (sel.has(e.id) ? { ...e, selected: true } : e))
    })
  }, [derivedEdges, setEdges])

  // ── layout persistence ──────────────────────────────────────────────────
  // Any move snapshots EVERY box, so later model edits never re-lay-out boxes
  // the author has placed.
  const snapshotLayout = useCallback((extra?: Record<string, { x: number; y: number }>) => {
    const cur = layoutRef.current.nodes || {}
    const next: NonNullable<WizardLayout['nodes']> = {}
    for (const n of rf.getNodes()) {
      next[n.id] = { x: Math.round(n.position.x), y: Math.round(n.position.y) }
      const w = n.width ?? cur[n.id]?.w
      if (w) next[n.id].w = Math.round(w)
    }
    for (const [id, p] of Object.entries(extra || {})) next[id] = { ...(next[id] || {}), x: Math.round(p.x), y: Math.round(p.y) }
    onLayoutChange({ ...layoutRef.current, nodes: next })
  }, [rf, onLayoutChange])

  const onNodesChange = useCallback((changes: NodeChange<WizardFlowNode>[]) => {
    onNodesChangeBase(changes)
    // A resize ends with a dimensions change carrying resizing: false.
    if (changes.some((c) => c.type === 'dimensions' && c.resizing === false)) {
      requestAnimationFrame(() => snapshotLayout())
    }
  }, [onNodesChangeBase, snapshotLayout])

  const onNodeDragStop = useCallback(() => { if (!readOnly) snapshotLayout() }, [readOnly, snapshotLayout])

  const autoArrange = useCallback(() => {
    const pos = autoLayout(modelRef.current)
    const nodesNext: NonNullable<WizardLayout['nodes']> = {}
    for (const [id, p] of Object.entries(pos)) {
      nodesNext[id] = { ...p }
      const w = layoutRef.current.nodes?.[id]?.w
      if (w) nodesNext[id].w = w
    }
    onLayoutChange({ ...layoutRef.current, nodes: nodesNext })
    requestAnimationFrame(() => rf.fitView({ padding: 0.2, duration: 300 }))
  }, [onLayoutChange, rf])

  // ── routes ──────────────────────────────────────────────────────────────
  const isValidConnection = useCallback<IsValidConnection<WizardFlowEdge>>((c) => {
    if (readOnly || !c.source || !c.target || !c.sourceHandle) return false
    if (c.target === START_ID || c.source === c.target || c.sourceHandle === 'aux') return false
    if (c.source === START_ID && c.target === FINISH_ID) return false
    if (c.target === FINISH_ID && !portTakesFinish(c.sourceHandle)) return false
    return true
  }, [readOnly])

  const onConnect = useCallback((c: Connection) => {
    if (!c.source || !c.target || !c.sourceHandle) return
    const next = connectRoute(modelRef.current, c.source, c.sourceHandle, c.target)
    if (next === modelRef.current) return
    onChange(next)
    if (c.sourceHandle === 'case:new') {
      onSelect(c.source)
      setPanelOpen(true)
      toast('Case added — write its condition in the panel', { icon: '✏️' })
    }
  }, [onChange, onSelect])

  const onReconnect = useCallback((old: WizardFlowEdge, c: Connection) => {
    const route = old.data?.route
    if (!route || route.readonly || !c.source || !c.target || !c.sourceHandle) return
    let m = modelRef.current
    if (c.source !== route.from || c.sourceHandle !== route.port) {
      if (!route.implicit) m = disconnectRoute(m, route.from, route.port)
    }
    m = connectRoute(m, c.source, c.sourceHandle, c.target)
    if (m !== modelRef.current) onChange(m)
  }, [onChange])

  const onBeforeDelete = useCallback<OnBeforeDelete<WizardFlowNode, WizardFlowEdge>>(async ({ nodes: ns, edges: es }) => {
    if (readOnly) return false
    return {
      nodes: ns.filter((n) => n.id !== START_ID && n.id !== FINISH_ID),
      edges: es.filter((e) => e.deletable !== false),
    }
  }, [readOnly])

  const onDelete = useCallback(({ nodes: ns, edges: es }: { nodes: WizardFlowNode[]; edges: WizardFlowEdge[] }) => {
    let m = modelRef.current
    const gone = new Set(ns.map((n) => n.id))
    for (const e of es) {
      const r = e.data?.route
      if (r && !gone.has(r.from) && !r.readonly && !r.implicit) m = disconnectRoute(m, r.from, r.port)
    }
    for (const id of gone) m = removeStep(m, id)
    if (m !== modelRef.current) onChange(m)
    if (selected && gone.has(selected)) { onSelect(null); setPanelOpen(false) }
  }, [onChange, onSelect, selected])

  // ── adding steps (IOFlow's addNode + drag from the panel) ────────────────
  const addStep = useCallback((kind: WizardStepKind, at?: { x: number; y: number }) => {
    if (readOnly) return
    const m = modelRef.current
    const id = newStepId(m, kind)
    const fresh = blankStep(kind, id)
    const ref = !at && selected ? m.steps.find((s) => s.id === selected) || null : null
    const steps = [...m.steps]
    let pos = at
    if (ref) {
      // Insert after the selected step and splice it into its route.
      const i = steps.indexOf(ref)
      const out = ref.next
      const spliceable = hasNextHandle(ref) && (typeof out === 'string' || (out && !Array.isArray(out)))
      if (spliceable && hasNextHandle(fresh)) {
        fresh.next = out
        steps[i] = { ...ref, next: id }
      }
      steps.splice(i + 1, 0, fresh)
      const rn = rf.getNode(ref.id)
      if (rn) pos = { x: rn.position.x, y: rn.position.y + (rn.measured?.height ?? nodeHeight(ref)) + 80 }
    } else {
      // Appended last: a last step that relied on "the following step" (none,
      // so it finished) is made to finish explicitly, so nothing re-routes.
      const last = steps[steps.length - 1]
      if (last && last.next === undefined && hasNextHandle(last)) {
        steps[steps.length - 1] = { ...last, next: { finish: true } }
      }
      steps.push(fresh)
      if (!pos) {
        const lowest = rf.getNodes().filter((n) => n.id !== FINISH_ID)
          .reduce((a, n) => Math.max(a, n.position.y + (n.measured?.height ?? 80)), 0)
        pos = { x: 0, y: lowest + 80 }
      }
    }
    onChange({ ...m, steps, start: m.start || id })
    if (pos) snapshotLayout({ [id]: pos })
    onSelect(id)
    setPanelOpen(true)
    setShowPalette(false)
  }, [readOnly, selected, rf, onChange, snapshotLayout, onSelect])

  const onDragOver = useCallback((e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes(STEP_MIME)) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }, [])

  const onDrop = useCallback((e: React.DragEvent) => {
    const kind = e.dataTransfer.getData(STEP_MIME) as WizardStepKind
    if (!kind || !STEP_KINDS.includes(kind)) return
    e.preventDefault()
    const p = rf.screenToFlowPosition({ x: e.clientX, y: e.clientY })
    addStep(kind, { x: p.x - NODE_W / 2, y: p.y - 30 })
  }, [rf, addStep])

  // ── the properties panel ────────────────────────────────────────────────
  const step = useMemo(() => model.steps.find((s) => s.id === selected) || null, [model.steps, selected])
  const panelTarget: 'start' | 'finish' | 'step' | null = selected === START_ID ? 'start'
    : selected === FINISH_ID ? 'finish' : step ? 'step' : null

  const patchStep = useCallback((id: string, p: Partial<WizardStep>) => {
    const m = modelRef.current
    onChange({
      ...m,
      steps: m.steps.map((s) => {
        if (s.id !== id) return s
        const merged = { ...s, ...p } as Record<string, unknown>
        for (const k of Object.keys(merged)) if (merged[k] === undefined) delete merged[k]
        return merged as unknown as WizardStep
      }),
    })
  }, [onChange])

  const renameSelected = useCallback((to: string) => {
    const m = modelRef.current
    if (!step || m.steps.some((s) => s.id === to && s !== step)) return
    onChange(renameStep(m, step.id, to))
    onLayoutChange(renameInLayout(layoutRef.current, step.id, to))
    onSelect(to)
  }, [step, onChange, onLayoutChange, onSelect])

  const deleteSelected = useCallback(() => {
    if (!step || readOnly) return
    onChange(removeStep(modelRef.current, step.id))
    onSelect(null)
    setPanelOpen(false)
  }, [step, readOnly, onChange, onSelect])

  const cyclePanelMode = useCallback(() => {
    setPanelMode((cur) => {
      const next = PANEL_MODES[(PANEL_MODES.indexOf(cur) + 1) % PANEL_MODES.length]
      try { localStorage.setItem(PANEL_MODE_KEY, next) } catch { /* private window */ }
      return next
    })
  }, [])

  const validate = useCallback(async () => {
    setValidating(true)
    try {
      await onValidate()
    } finally {
      setValidating(false)
    }
  }, [onValidate])

  const issueCount = (check?.errors.length || 0) + (check?.warnings.length || 0)

  const panelBody = panelOpen && panelTarget && (
    <PropertiesPanel
      mode={panelMode}
      title={panelTarget === 'start' ? 'Start — the wizard' : panelTarget === 'finish' ? 'Finish' : (step?.title || KIND_META[step!.kind]?.label || step!.id)}
      subtitle={panelTarget === 'step' ? `${KIND_META[step!.kind]?.label || step!.kind} · ID: ${step!.id}` : 'Base & Mode'}
      tone={panelTarget === 'step' ? CATEGORY_TONE[CATEGORY_OF[step!.kind] as Category] : CATEGORY_TONE[panelTarget === 'start' ? 'start' : 'finish']}
      canDelete={panelTarget === 'step' && !readOnly}
      onDelete={deleteSelected}
      onCycleMode={cyclePanelMode}
      onClose={() => setPanelOpen(false)}
    >
      {panelTarget === 'step' && step && (
        <StepInspector key={model.steps.indexOf(step)} step={step}
          onChange={(p) => patchStep(step.id, p)} onRename={renameSelected} />
      )}
      {panelTarget !== 'step' && <BaseTab onChange={onChange} />}
    </PropertiesPanel>
  )

  return (
    <div className="tw-wzflow" data-tw-wizard-flow-designer="" style={{
      position: 'relative', height: '100%', minHeight: 480, border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-md, 6px)', overflow: 'hidden', background: 'var(--color-bg)',
    }}>
      <ReactFlow<WizardFlowNode, WizardFlowEdge>
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onReconnect={onReconnect}
        isValidConnection={isValidConnection}
        onBeforeDelete={onBeforeDelete}
        onDelete={onDelete}
        onNodeDragStop={onNodeDragStop}
        onNodeClick={(_, n) => { onSelect(n.id); setPanelOpen(true); setShowPalette(false) }}
        onPaneClick={() => setShowPalette(false)}
        onDragOver={onDragOver}
        onDrop={onDrop}
        nodesDraggable={!readOnly}
        nodesConnectable={!readOnly}
        edgesReconnectable={!readOnly}
        deleteKeyCode={readOnly ? null : ['Delete', 'Backspace']}
        colorMode={theme === 'light' ? 'light' : 'dark'}
        connectionLineStyle={{ stroke: 'var(--color-accent)', strokeWidth: 2, strokeDasharray: '5 5' }}
        defaultEdgeOptions={{ type: 'wzRoute' }}
        fitView
        fitViewOptions={{ padding: 0.2, maxZoom: 1.2 }}
        minZoom={0.2}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
        <Controls showInteractive={false} />
        {showMiniMap && (
          <MiniMap pannable zoomable
            nodeColor={(n) => MINIMAP_COLOR[n.type || '']
              || CATEGORY_TONE[(n.data as { category?: Category }).category || 'system']} />
        )}

        <Panel position="top-left">
          <div style={{ display: 'flex', gap: 6 }}>
            {!readOnly && (
              <button className="btn btn-secondary btn-sm" data-tw-action="wizard-flow-add"
                onClick={() => setShowPalette((v) => !v)}
                title={selected && step ? `Add a step after “${step.title || step.id}”` : 'Add a step'}>
                <Plus size={14} /> Add step
              </button>
            )}
            {!readOnly && (
              <>
                <button className="btn btn-ghost btn-sm" onClick={history.undo} disabled={!history.canUndo} title="Undo (Ctrl+Z)"><Undo2 size={14} /></button>
                <button className="btn btn-ghost btn-sm" onClick={history.redo} disabled={!history.canRedo} title="Redo (Ctrl+Shift+Z)"><Redo2 size={14} /></button>
              </>
            )}
          </div>
        </Panel>

        <Panel position="top-right">
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => void validate()} disabled={validating}
              title="Run the Check and mark the boxes with what it finds" data-tw-action="wizard-flow-validate">
              {validating ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                : issueCount ? <AlertTriangle size={14} style={{ color: check?.errors.length ? 'var(--color-error)' : 'var(--color-warning)' }} />
                  : <CheckCircle2 size={14} style={{ color: check ? 'var(--color-success)' : undefined }} />}
              Validate{issueCount ? ` (${issueCount})` : ''}
            </button>
            {!readOnly && (
              <button className="btn btn-ghost btn-sm" onClick={autoArrange} title="Lay every box out again from Start">
                <LayoutGrid size={14} /> Arrange
              </button>
            )}
            <button className="btn btn-ghost btn-sm" onClick={() => setShowMiniMap((v) => !v)} title="Show / hide the minimap">
              <MapIcon size={14} />
            </button>
          </div>
        </Panel>

        {general.errors.length + general.warnings.length > 0 && (
          <Panel position="bottom-center">
            <div style={{
              maxWidth: 520, padding: '8px 12px', borderRadius: 6, fontSize: 13,
              background: 'var(--color-bg-secondary)', border: `1px solid ${general.errors.length ? 'var(--color-error)' : 'var(--color-warning)'}`,
            }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Wizard-level findings</div>
              {general.errors.map((m, i) => <div key={`e${i}`} style={{ color: 'var(--color-error)' }}>● {m}</div>)}
              {general.warnings.map((m, i) => <div key={`w${i}`} style={{ color: 'var(--color-warning)' }}>▲ {m}</div>)}
            </div>
          </Panel>
        )}

        <Panel position="bottom-left" style={{ marginLeft: 56 }}>
          <Legend />
        </Panel>
      </ReactFlow>

      {showPalette && !readOnly && (
        <StepPalette onAdd={(k) => addStep(k)} anchorTitle={step ? (step.title || step.id) : null}
          onClose={() => setShowPalette(false)} />
      )}

      {panelBody && (panelMode === 'right' ? panelBody : createPortal(panelBody, document.body))}
    </div>
  )
}

// ── IOFlow's "Add Node" panel ───────────────────────────────────────────────

const PALETTE_GROUPS: { title: string; kinds: WizardStepKind[] }[] = [
  { title: 'Screens', kinds: STEP_KINDS.filter((k) => CATEGORY_OF[k] === 'screen') },
  { title: CATEGORY_LABEL.system, kinds: STEP_KINDS.filter((k) => CATEGORY_OF[k] === 'system') },
  { title: 'Flow control', kinds: STEP_KINDS.filter((k) => CATEGORY_OF[k] === 'branch' || CATEGORY_OF[k] === 'repeat') },
  { title: 'Save and other wizards', kinds: STEP_KINDS.filter((k) => CATEGORY_OF[k] === 'save' || CATEGORY_OF[k] === 'call') },
]

function StepPalette({ onAdd, onClose, anchorTitle }: {
  onAdd: (k: WizardStepKind) => void
  onClose: () => void
  anchorTitle: string | null
}) {
  return (
    <div data-tw-wizard-flow-palette="" style={{
      position: 'absolute', top: 52, left: 12, zIndex: 20, width: 290, maxHeight: 'calc(100% - 70px)',
      overflowY: 'auto', padding: 12, borderRadius: 8, background: 'var(--color-bg-secondary)',
      border: '1px solid var(--color-border)', boxShadow: '0 8px 24px var(--color-shadow, rgba(0,0,0,.3))',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>Add step</div>
        <button className="btn btn-ghost btn-sm" onClick={onClose} title="Close"><X size={14} /></button>
      </div>
      <div style={{ ...hintStyle, marginTop: 0, marginBottom: 8 }}>
        {anchorTitle ? `Click to add after “${anchorTitle}”, or drag onto the canvas.` : 'Click to add, or drag onto the canvas.'}
      </div>
      {PALETTE_GROUPS.map((g) => (
        <div key={g.title} style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: 'var(--color-text-secondary)', margin: '4px 0' }}>
            {g.title}
          </div>
          {g.kinds.map((k) => {
            const tone = CATEGORY_TONE[CATEGORY_OF[k]]
            return (
              <button key={k} type="button" draggable data-tw-wizard-flow-kind={k}
                onDragStart={(e) => { e.dataTransfer.setData(STEP_MIME, k); e.dataTransfer.effectAllowed = 'move' }}
                onClick={() => onAdd(k)}
                style={{
                  width: '100%', textAlign: 'left', display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '7px 9px', marginBottom: 4, borderRadius: 6, cursor: 'grab',
                  border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)',
                }}>
                <span className="material-icons" style={{ fontSize: 19, color: tone, marginTop: 1 }}>{KIND_META[k].icon}</span>
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600 }}>{KIND_META[k].label}</span>
                  <span style={{ display: 'block', fontSize: 13, color: 'var(--color-text-secondary)' }}>{KIND_META[k].hint}</span>
                </span>
              </button>
            )
          })}
        </div>
      ))}
    </div>
  )
}

// ── IOFlow's properties panel (right / center / full screen) ────────────────

function PropertiesPanel({
  mode, title, subtitle, tone, canDelete, onDelete, onCycleMode, onClose, children,
}: {
  mode: PanelMode
  title: string
  subtitle: string
  tone: string
  canDelete: boolean
  onDelete: () => void
  onCycleMode: () => void
  onClose: () => void
  children: React.ReactNode
}) {
  useEffect(() => {
    if (mode === 'right') return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mode, onClose])

  const frame: React.CSSProperties = mode === 'fullscreen'
    ? { position: 'fixed', inset: 0, zIndex: 1100 }
    : mode === 'center'
      ? { position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 'min(90vw, 1100px)', height: '82vh', zIndex: 1100, borderRadius: 8 }
      : { position: 'absolute', top: 8, right: 8, bottom: 8, width: 'min(480px, calc(100% - 16px))', zIndex: 20, borderRadius: 8 }
  const ModeIcon = mode === 'right' ? Columns2 : mode === 'center' ? Square : Maximize
  return (
    <>
      {mode === 'center' && (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 1099, background: 'rgba(0,0,0,0.3)' }} />
      )}
      <div data-tw-wizard-flow-panel={mode} style={{
        ...frame, display: 'flex', flexDirection: 'column', background: 'var(--color-bg)',
        border: '1px solid var(--color-border)', boxShadow: '0 8px 28px var(--color-shadow, rgba(0,0,0,.35))',
        borderTop: `3px solid ${tone}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, padding: '10px 12px 8px' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
            <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', fontFamily: 'monospace' }}>{subtitle}</div>
          </div>
          <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
            {canDelete && (
              <button className="btn btn-ghost btn-sm" onClick={onDelete} title="Delete this step" style={{ color: 'var(--color-error)' }}>
                <Trash2 size={15} />
              </button>
            )}
            <button className="btn btn-ghost btn-sm" onClick={onCycleMode} title={`View: ${mode} — switch`}>
              {mode === 'fullscreen' ? <Maximize2 size={15} /> : <ModeIcon size={15} />}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={onClose} title="Close"><X size={15} /></button>
          </div>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 12px 12px' }}>
          {children}
        </div>
      </div>
    </>
  )
}

function Legend() {
  const items: [string, string, string | undefined][] = [
    ['next', '#94a3b8', undefined], ['condition', '#f59e0b', undefined], ['per line', '#a855f7', undefined],
    ['call returns', '#6366f1', undefined], ['error / issue', '#ef4444', '6 4'],
    ['following step', 'var(--color-text-faint, #687281)', '6 4'], ['op / tool goto', 'var(--color-text-faint, #687281)', '2 4'],
  ]
  return (
    <div style={{
      display: 'flex', gap: 10, flexWrap: 'wrap', maxWidth: 520, padding: '4px 8px', borderRadius: 6, fontSize: 13,
      background: 'color-mix(in srgb, var(--color-bg-secondary) 88%, transparent)', border: '1px solid var(--color-border)',
      color: 'var(--color-text-secondary)',
    }}>
      {items.map(([label, stroke, dash]) => (
        <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <svg width={20} height={6} style={{ maxWidth: 'none' }}><line x1={0} y1={3} x2={20} y2={3} stroke={stroke} strokeWidth={2} strokeDasharray={dash} /></svg>
          {label}
        </span>
      ))}
    </div>
  )
}
